import React from "react";
// 1. Import Helmet
import { Helmet } from "react-helmet";
import Header from "../Components/Header";
import Footer from "../Components/Footer";

// Define metadata constants for SEO
const PAGE_TITLE = "Privacy Policy | Steminorder Data Protection and Usage";
const PAGE_DESCRIPTION = "Review the official Privacy Policy for Steminorder. Learn how we collect, use, and protect your personal information on our maritime platform.";
const CANONICAL_URL = "https://www.steminorder.com/privacy-policy";

const PrivacyPolicy = () => {
  return (
    <>
      {/* ============================================================
        REACT HELMET INTEGRATION 
        ============================================================
      */}
      <Helmet>
        <title>{PAGE_TITLE}</title>
        <meta name="description" content={PAGE_DESCRIPTION} />
        
        {/* Canonical URL */}
        <link rel="canonical" href={CANONICAL_URL} />

        {/* Open Graph Tags */}
        <meta property="og:title" content={PAGE_TITLE} />
        <meta property="og:description" content={PAGE_DESCRIPTION} />
        <meta property="og:url" content={CANONICAL_URL} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Steminorder" />
        {/* No specific image needed for a privacy policy, but included for structure */}
        
        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={PAGE_TITLE} />
        <meta name="twitter:description" content={PAGE_DESCRIPTION} />
      </Helmet>
      
      <Header/>
      <div className="container py-10 text-gray-700 leading-relaxed">
        <h1 className="text-3xl font-semibold mb-6">
          Privacy Policy
        </h1>
        <p className="mb-4">
          This **Privacy Policy** explains how Steminorder (“we,” “our,” or “us”)
          collects, uses, and protects your personal information when you use our
          website, platform, and services.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-3">1. Information We Collect</h2>
        <p className="mb-4">
          We collect information you provide directly to us, such as when you
          create an account, contact support, or submit forms. This may include:
        </p>
        <ul className="list-disc list-inside mb-4">
          <li>Full name and contact details (**email, phone number**)</li>
          <li>Company or organization details (if applicable)</li>
          <li>Usage data, including pages visited and interaction logs</li>
          <li>Technical data such as **IP address** and browser type</li>
        </ul>

        <h2 className="text-xl font-semibold mt-8 mb-3">2. How We Use Your Information</h2>
        <p className="mb-4">We use collected data to:</p>
        <ul className="list-disc list-inside mb-4">
          <li>Provide and maintain our services</li>
          <li>Improve user experience and platform performance</li>
          <li>Communicate updates, news, and support messages</li>
          <li>Ensure platform security and prevent fraudulent activity</li>
        </ul>

        <h2 className="text-xl font-semibold mt-8 mb-3">3. Data Sharing and Disclosure</h2>
        <p className="mb-4">
          We do not **sell or rent** your personal data. We may share it with trusted
          third-party service providers who help operate our platform, but only
          under strict confidentiality agreements.
        </p>
        <p className="mb-4">
          We may also disclose data if required by law, to protect our rights, or
          to prevent illegal activities.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-3">4. Data Security</h2>
        <p className="mb-4">
          We take appropriate **technical and organizational measures** to protect
          your personal data against unauthorized access, alteration, disclosure,
          or destruction.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-3">5. Cookies and Tracking</h2>
        <p className="mb-4">
          Our website may use **cookies** to improve functionality and personalize
          your experience. You can disable cookies in your browser settings, but
          some features may not work as intended.
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-3">6. Your Rights</h2>
        <p className="mb-4">
          You have the right to **access, update, or delete** your personal
          information. To exercise these rights, please contact us at{" "}
          <a
            href="mailto:info@steminorder.com"
            className="text-blue-600 hover:underline"
          >
            info@steminorder.com
          </a>
          .
        </p>

        <h2 className="text-xl font-semibold mt-8 mb-3">7. Changes to This Policy</h2>
        <p className="mb-4">
          We may update this Privacy Policy periodically. Any changes will be
          reflected on this page with a new “Last Updated” date.
        </p>

        <p className="text-sm text-gray-500 mt-8">
          **Last Updated:** October 2025
        </p>
      </div>
      <Footer />
    </>
  );
};

export default PrivacyPolicy;